import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, FormControlLabel, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import { CodeBlock, Expect, Hint, Step, useSteps } from "../guide/GuideParts";
import { Flow, FlowActor, FlowStep, LinkCards, PageHeader, Screenshot, Section } from "../guide/DocParts";

// Material data (CellData stock exchange values) for the Handelsapp and the Material Data web app

const RG = "https://portal.azure.com/#@genesiscloud.onmicrosoft.com/resource/subscriptions/2ee57a8d-3069-49cb-8889-ad7e370a791c/resourceGroups/gsc-r1-pr-rg-materialdata-1/providers";
const FUNCTION_APP = `${RG}/Microsoft.Web/sites/ImportMaterialDataFunctions/appServices`;
const FUNCTIONS_LIST = `${RG}/Microsoft.Web/sites/ImportMaterialDataFunctions/functionsList`;
const INSIGHTS = `${RG}/microsoft.insights/components/ImportMaterialDataFunctions/failures`;

const ACTORS: FlowActor[] = [
  { id: "cell", name: "CellData", sub: "SOAP service", color: "#e07b00" },
  { id: "fn", name: "ImportMaterialDataFunctions", sub: "Azure Function App", color: "#0078d4" },
  { id: "st", name: "gscr1prstomaterialdata", sub: "Storage tables + queue", color: "#7b4fc9" },
  { id: "api", name: "MaterialData API", sub: "via CentralGateway", color: "#00897b" },
  { id: "apps", name: "Handelsapp · Material UI", sub: "AD users", color: "#0277bd" },
];

const FLOW: FlowStep[] = [
  { from: "fn", to: "fn", text: "A durable timer (DurableExchangeEvents) starts the collection in the time windows below, Monday to Friday." },
  { from: "fn", to: "cell", text: "The activity functions Currencies, LowestMetals and OfficialMetals call the CellData SOAP service. CellData only accepts our registered IP addresses." },
  { from: "cell", to: "fn", text: "CellData returns the exchange values of the day." },
  { from: "fn", to: "st", text: "The values are stored in the tables of gscr1prstomaterialdata. A message in the queue importjobs starts an import by hand." },
  { from: "api", to: "st", text: "The MaterialData API reads the current and historical values." },
  { from: "apps", to: "api", text: "Handelsapp (mobile) and the Material Data web app call it through the CentralGateway with the user's AD token." },
];

interface Window {
  id: string;
  name: string;
  from: number;
  to: number;
  message?: string;
}

const WINDOWS: Window[] = [
  { id: "official", name: "Official metals", from: 14.5, to: 15 + 50 / 60 },
  { id: "currencies", name: "Currencies", from: 18, to: 20, message: "Currencies" },
  { id: "lowest", name: "Lowest metals", from: 18, to: 20, message: "LowestMetalValues" },
];

const FUNCTIONS: [string, string][] = [
  ["Currencies", "Activity"],
  ["DurableExchangeEvents", "Orchestration"],
  ["DurableExchangeEvents_RunTimer", "Timer"],
  ["LowestMetals", "Activity"],
  ["OfficialMetals", "Activity"],
];

const time = (h: number) => `${String(Math.floor(h)).padStart(2, "0")}:${String(Math.round((h % 1) * 60)).padStart(2, "0")}`;

// Last weekday collection that should already be done
const expectedSince = (w: Window, now: Date) => {
  const d = new Date(now);
  const hour = now.getHours() + now.getMinutes() / 60;
  const weekday = (x: Date) => x.getDay() >= 1 && x.getDay() <= 5;
  if (!(weekday(d) && hour >= w.to)) {
    do d.setDate(d.getDate() - 1);
    while (!weekday(d));
  }
  d.setHours(Math.floor(w.to), Math.round((w.to % 1) * 60), 0, 0);
  return d;
};

const Freshness: React.FunctionComponent = () => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const hour = now.getHours() + now.getMinutes() / 60;
  const weekday = now.getDay() >= 1 && now.getDay() <= 5;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 1.5 }}>
      {WINDOWS.map((w) => {
        const running = weekday && hour >= w.from && hour < w.to;
        const since = expectedSince(w, now);
        return (
          <Paper key={w.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderTop: `4px solid ${running ? "#e5ac58" : "#43a047"}` }}>
            <Typography sx={{ fontWeight: 700 }}>{w.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Mon–Fri {time(w.from)}–{time(w.to)}
            </Typography>
            <Box sx={{ position: "relative", height: 10, borderRadius: 5, my: 1, background: (t) => alpha(t.palette.text.primary, 0.06) }}>
              <Box sx={{ position: "absolute", top: 0, bottom: 0, borderRadius: 5, left: `${(w.from / 24) * 100}%`, width: `${((w.to - w.from) / 24) * 100}%`, background: "#0078d4" }} />
              <Box sx={{ position: "absolute", top: -3, bottom: -3, width: 2, left: `${(hour / 24) * 100}%`, background: "#e53935" }} />
            </Box>
            <Typography variant="body2">
              {running ? (
                <b>Collecting right now</b>
              ) : (
                <>
                  Values should be from <b>{since.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "2-digit" })}</b> or newer
                </>
              )}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

const Troubleshoot: React.FunctionComponent = () => {
  const [which, setWhich] = React.useState("currencies");
  const window = WINDOWS.find((w) => w.id === which)!;
  const { isDone, toggle, count } = useSteps(which);
  return (
    <>
      <Typography variant="subtitle2">Which values are old?</Typography>
      <RadioGroup row value={which} onChange={(e) => setWhich(e.target.value)}>
        {WINDOWS.map((w) => (
          <FormControlLabel key={w.id} value={w.id} control={<Radio size="small" />} label={w.name} />
        ))}
      </RadioGroup>
      <Typography variant="caption" color="text.secondary">
        {count} of 6 checks done · stop at the first one that explains it
      </Typography>
      <Step n={1} title="Is it really late?" where="browser" done={isDone(1)} onToggle={toggle(1)}>
        <p>
          {window.name} are collected Monday to Friday {time(window.from)}–{time(window.to)}. Before that, yesterday's values are correct. The cards above show which date to expect.
        </p>
      </Step>
      <Step n={2} title="Was the London exchange closed?" where="browser" done={isDone(2)} onToggle={toggle(2)}>
        <p>On a London Metal Exchange holiday there are no new values. Nothing to do – they come on the next trading day.</p>
      </Step>
      <Step n={3} title="Is the Function App running?" where="azure" done={isDone(3)} onToggle={toggle(3)}>
        <p>
          Open <a href={FUNCTION_APP} target="_blank" rel="noreferrer">ImportMaterialDataFunctions</a>. Status must be <b>Running</b>; if it hangs, click <b>Restart</b>.
        </p>
        <Screenshot src="/AzureFunctions.PNG" caption="ImportMaterialDataFunctions → Overview → Restart" />
      </Step>
      <Step n={4} title="Look at the errors" where="azure" done={isDone(4)} onToggle={toggle(4)}>
        <p>
          <a href={INSIGHTS} target="_blank" rel="noreferrer">Application Insights → Failures</a> shows failed runs and their exceptions; the{" "}
          <a href={FUNCTIONS_LIST} target="_blank" rel="noreferrer">functions list</a> shows the last executions. Timeouts or “access denied” from CellData → step 6.
        </p>
      </Step>
      <Step n={5} title="Start the import by hand" where="storage" done={isDone(5)} onToggle={toggle(5)}>
        {window.message ? (
          <>
            <p>
              Azure Storage Explorer → webapp1 → Storage Accounts → <b>gscr1prstomaterialdata</b> → Queues → <b>importjobs</b> → <b>Add Message</b> with the text:
            </p>
            <CodeBlock code={window.message} />
            <Expect>the message disappears from the queue after a few seconds and new values are in the table. If it moves to importjobs-poison, the import failed – see step 4.</Expect>
            <Screenshot src="/importjobs.png" caption="Queue importjobs: Add Message “Currencies” or “LowestMetalValues”" />
          </>
        ) : (
          <p>Official metals have no documented queue message; restart the Function App (step 3) within the collection window instead.</p>
        )}
      </Step>
      <Step n={6} title="CellData refuses us: check our IP addresses" where="azure" done={isDone(6)} onToggle={toggle(6)}>
        <p>
          CellData only accepts registered IPs. If Azure changed the outbound IPs of the Function App, the calls fail. Find the current ones in Function App → Networking → Outbound
          addresses, or:
        </p>
        <CodeBlock label="Azure CLI" code={`az functionapp show -g gsc-r1-pr-rg-materialdata-1 -n ImportMaterialDataFunctions --query outboundIpAddresses -o tsv`} />
        <p>
          Send the new IPs to the CellData administrators (contact in the APPS Directory → Handelsapp Material Data) and ask them to allow them. Service:{" "}
          <code>http://csp.cell-data.it/wsCellData.asmx?WSDL</code>
        </p>
      </Step>
    </>
  );
};

export const MaterialDataPage: React.FunctionComponent = () => (
  <Paper sx={{ p: 2, width: "100%" }}>
    <PageHeader
      title="Material Data & Handelsapp"
      subtitle="Stock exchange values (currencies, metals) from CellData for the Handelsapp and the Material Data web app"
      stats={[
        { label: "Azure functions", value: FUNCTIONS.length },
        { label: "Collections / day", value: WINDOWS.length },
        { label: "Clients", value: 2 },
      ]}
    />

    <Section title="How the data flows" text="Press Play or click the numbers">
      <Flow actors={ACTORS} steps={FLOW} />
    </Section>

    <Section title="Should the values be up to date now?">
      <Freshness />
    </Section>

    <Section title="The data is not current – what now?">
      <Troubleshoot />
    </Section>

    <Section title="Clients">
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderTop: "4px solid #0277bd" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Handelsapp (iOS & Android)
          </Typography>
          <Typography variant="body2">
            React Native app, distributed with Microsoft Intune. AD users of the B2C directory see the current CellData values and the news of the managers.
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderTop: "4px solid #00897b" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Material Data web app
          </Typography>
          <Typography variant="body2">For AD users: current and historical CellData values, the job status, and creating the news for the Handelsapp.</Typography>
        </Paper>
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Functions of ImportMaterialDataFunctions
      </Typography>
      <Box component="table" sx={{ borderCollapse: "collapse", fontSize: 13, "& td, & th": { p: 0.6, pr: 3, borderBottom: 1, borderColor: "divider", textAlign: "left" } }}>
        <thead>
          <tr>
            <th>Function</th>
            <th>Trigger</th>
          </tr>
        </thead>
        <tbody>
          {FUNCTIONS.map(([n, t]) => (
            <tr key={n}>
              <td>
                <code>{n}</code>
              </td>
              <td>{t}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Section>

    <Section title="Links">
      <LinkCards
        links={[
          { title: "Material Data web app", url: "https://materialdata.genesiscloud.cc/", color: "#00897b" },
          { title: "Function App", text: "Restart, status", url: FUNCTION_APP, color: "#0078d4" },
          { title: "Function executions", url: FUNCTIONS_LIST, color: "#0078d4" },
          { title: "Application Insights", text: "Failures", url: INSIGHTS, color: "#5c2d91" },
          { title: "CentralGateway API", url: "https://gsc-r1-pr-ase-centralgateway-1.azurewebsites.net/api", color: "#005f98" },
          { title: "Handelsapp in B2C", text: "App registration", url: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Overview/appId/9717b66a-3fd6-41ab-8fb9-9df24183cf3d/isMSAApp~/false", color: "#5c2d91" },
        ]}
      />
      <Hint>The Sandbox monitor always shows the last values of the currencies table – a quick way to check the import.</Hint>
    </Section>
  </Paper>
);
